import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { customerCipaService } from '../../services/cipa.service';
import { useToast, ToastContainer } from '../../components/Toast';
import {
  ShieldCheck,
  Users,
  Calendar,
  ClipboardList,
  Vote,
  Award,
  Clock,
  RefreshCw,
  UserPlus,
  FileText,
  Info,
  Building2,
  Download,
} from 'lucide-react';
import type {
  CipaAcaoContract,
  CipaMandatoContract,
  CipaMembroContract,
  CipaProcessoEleitoralContract,
  CipaReuniaoContract,
  IndicarBancadaEmpregadorDto,
} from '@sstexames/shared-types';

type CipaTabType = 'quadro' | 'reunioes' | 'eleicao' | 'acoes';

export default function CipaClientePage() {
  const { empresa } = useEmpresa();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState<CipaTabType>('quadro');

  // Dados do Mandato
  const [loadingMandato, setLoadingMandato] = useState(false);
  const [mandato, setMandato] = useState<CipaMandatoContract | null>(null);

  // Membros
  const [loadingMembros, setLoadingMembros] = useState(false);
  const [membros, setMembros] = useState<CipaMembroContract[]>([]);

  // Reuniões e Atas
  const [loadingReunioes, setLoadingReunioes] = useState(false);
  const [reunioes, setReunioes] = useState<CipaReuniaoContract[]>([]);
  const [selectedReuniaoAta, setSelectedReuniaoAta] = useState<CipaReuniaoContract | null>(null);

  // Processo Eleitoral
  const [loadingProcesso, setLoadingProcesso] = useState(false);
  const [processo, setProcesso] = useState<CipaProcessoEleitoralContract | null>(null);

  // Planos de Ação 5W2H
  const [loadingAcoes, setLoadingAcoes] = useState(false);
  const [acoes, setAcoes] = useState<CipaAcaoContract[]>([]);

  // Modal: Indicar Bancada do Empregador
  const [showBancadaModal, setShowBancadaModal] = useState(false);
  const [bancadaTitulares, setBancadaTitulares] = useState('');
  const [bancadaSuplentes, setBancadaSuplentes] = useState('');
  const [salvandoBancada, setSalvandoBancada] = useState(false);

  // 1. Carregar Mandato Atual
  const loadMandato = useCallback(async () => {
    try {
      setLoadingMandato(true);
      const data = await customerCipaService.getMandatoAtual();
      setMandato(data);
    } catch (err) {
      console.error('Erro ao carregar mandato CIPA:', err);
      toast.error('Erro ao obter dados do mandato da CIPA');
    } finally {
      setLoadingMandato(false);
    }
  }, [toast]);

  useEffect(() => {
    loadMandato();
  }, [loadMandato]);

  // 2. Carregar Membros
  const loadMembros = useCallback(async (mandatoId?: number) => {
    try {
      setLoadingMembros(true);
      const data = await customerCipaService.getMembros(mandatoId);
      setMembros(data);
    } catch (err) {
      console.error('Erro ao carregar membros CIPA:', err);
    } finally {
      setLoadingMembros(false);
    }
  }, []);

  // 3. Carregar Reuniões
  const loadReunioes = useCallback(async (mandatoId?: number) => {
    try {
      setLoadingReunioes(true);
      const data = await customerCipaService.getReunioes(mandatoId);
      setReunioes(data);
    } catch (err) {
      console.error('Erro ao carregar reuniões CIPA:', err);
    } finally {
      setLoadingReunioes(false);
    }
  }, []);

  // 4. Carregar Processo Eleitoral
  const loadProcesso = useCallback(async (mandatoId?: number) => {
    try {
      setLoadingProcesso(true);
      const data = await customerCipaService.getProcessoEleitoral(mandatoId);
      setProcesso(data);
    } catch (err) {
      console.error('Erro ao carregar processo eleitoral:', err);
    } finally {
      setLoadingProcesso(false);
    }
  }, []);

  // 5. Carregar Ações 5W2H
  const loadAcoes = useCallback(async (mandatoId?: number) => {
    try {
      setLoadingAcoes(true);
      const data = await customerCipaService.getAcoes(mandatoId);
      setAcoes(data);
    } catch (err) {
      console.error('Erro ao carregar ações 5W2H:', err);
    } finally {
      setLoadingAcoes(false);
    }
  }, []);

  // Disparar carregamento conforme aba ativa
  useEffect(() => {
    if (activeTab === 'quadro') {
      loadMembros(mandato?.id);
    } else if (activeTab === 'reunioes') {
      loadReunioes(mandato?.id);
    } else if (activeTab === 'eleicao') {
      loadProcesso(mandato?.id);
    } else if (activeTab === 'acoes') {
      loadAcoes(mandato?.id);
    }
  }, [activeTab, mandato?.id, loadMembros, loadReunioes, loadProcesso, loadAcoes]);

  // Salvar Bancada do Empregador
  const handleSalvarBancada = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mandato) return;

    const titulares = bancadaTitulares
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => !isNaN(n) && n > 0);
    const suplentes = bancadaSuplentes
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => !isNaN(n) && n > 0);

    if (titulares.length === 0) {
      toast.warning('Informe ao menos um ID de titular válido');
      return;
    }

    try {
      setSalvandoBancada(true);
      const dto: IndicarBancadaEmpregadorDto = {
        mandato_id: mandato.id,
        membros: [
          ...titulares.map((id) => ({
            colaborador_id: id,
            tipo_membro: 'TITULAR' as const,
          })),
          ...suplentes.map((id) => ({
            colaborador_id: id,
            tipo_membro: 'SUPLENTE' as const,
          })),
        ],
      };
      await customerCipaService.indicarBancadaEmpregador(dto);
      toast.success('Bancada do empregador indicada com sucesso!');
      setShowBancadaModal(false);
      loadMembros(mandato.id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao indicar bancada';
      toast.error(msg);
    } finally {
      setSalvandoBancada(false);
    }
  };

  // Separação de membros por bancada
  const membrosEmpregador = useMemo(
    () => membros.filter((m) => m.representacao === 'EMPREGADOR'),
    [membros]
  );
  const membrosEmpregados = useMemo(
    () => membros.filter((m) => m.representacao === 'EMPREGADOS'),
    [membros]
  );

  // Quórum Porcentagem
  const quorumPorcentagem = useMemo(() => {
    if (!processo || !processo.quorum_esperado || processo.quorum_esperado === 0) return 0;
    return Math.min(
      100,
      Math.round(((processo.votos_apurados || 0) / processo.quorum_esperado) * 100)
    );
  }, [processo]);

  return (
    <div className="space-y-6 pb-12">
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />

      {/* Header com Identidade Visual CIPA */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100 shadow-xs">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                CIPA & Comissões (NR-5)
              </h1>
              <span className="text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                Módulo Ativo
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Gestão participativa de segurança do trabalho, prevenção de acidentes e comissão interna
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              loadMandato();
              if (mandato?.id) {
                if (activeTab === 'quadro') loadMembros(mandato.id);
                if (activeTab === 'reunioes') loadReunioes(mandato.id);
                if (activeTab === 'eleicao') loadProcesso(mandato.id);
                if (activeTab === 'acoes') loadAcoes(mandato.id);
              }
            }}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            title="Atualizar dados"
          >
            <RefreshCw className={`w-4 h-4 ${loadingMandato ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Resumo do Mandato Atual */}
      {mandato ? (
        <div className="bg-linear-to-r from-emerald-500/10 via-teal-500/5 to-transparent border border-emerald-200/80 rounded-2xl p-5 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider">
                  Mandato Gestão {mandato.ano_exercicio}
                </span>
                <span
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    mandato.status === 'ATIVO'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {mandato.status}
                </span>
              </div>
              <p className="text-xs text-gray-600">
                Vigência oficial de {new Date(mandato.data_inicio).toLocaleDateString('pt-BR')} até{' '}
                {new Date(mandato.data_fim).toLocaleDateString('pt-BR')} • {empresa?.nome || 'Empresa'}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right">
                <span className="text-[11px] text-gray-500 block">Composição</span>
                <span className="text-xs font-bold text-gray-900">
                  {mandato.enquadramento?.modalidade === 'COMISSAO_COMPLETA' ? 'Comissão Paritária' : 'Designado CIPA'}
                </span>
              </div>
              <button
                onClick={() => setShowBancadaModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-white hover:bg-emerald-50 border border-emerald-300 rounded-xl shadow-2xs transition-colors"
              >
                <UserPlus className="w-3.5 h-3.5" />
                Indicar Empregador
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
          <Info className="w-8 h-8 text-amber-600 mx-auto mb-2" />
          <h3 className="text-sm font-bold text-amber-900">Nenhum mandato ativo cadastrado</h3>
          <p className="text-xs text-amber-700 mt-1 max-w-md mx-auto">
            O enquadramento da sua empresa e a criação do mandato são configurados junto à equipe técnica do SST. Em breve os dados estarão disponíveis.
          </p>
        </div>
      )}

      {/* Navegação por Abas */}
      <div className="flex border-b border-gray-200 space-x-1">
        <button
          onClick={() => setActiveTab('quadro')}
          className={`flex items-center gap-2 py-3 px-4 text-xs font-bold border-b-2 transition-colors ${
            activeTab === 'quadro'
              ? 'border-emerald-600 text-emerald-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Users className="w-4 h-4" />
          Quadro da CIPA & Membros
        </button>

        <button
          onClick={() => setActiveTab('reunioes')}
          className={`flex items-center gap-2 py-3 px-4 text-xs font-bold border-b-2 transition-colors ${
            activeTab === 'reunioes'
              ? 'border-emerald-600 text-emerald-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Calendar className="w-4 h-4" />
          Calendário & Atas de Reuniões
        </button>

        <button
          onClick={() => setActiveTab('eleicao')}
          className={`flex items-center gap-2 py-3 px-4 text-xs font-bold border-b-2 transition-colors ${
            activeTab === 'eleicao'
              ? 'border-emerald-600 text-emerald-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Vote className="w-4 h-4" />
          Processo Eleitoral & Urna
        </button>

        <button
          onClick={() => setActiveTab('acoes')}
          className={`flex items-center gap-2 py-3 px-4 text-xs font-bold border-b-2 transition-colors ${
            activeTab === 'acoes'
              ? 'border-emerald-600 text-emerald-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <ClipboardList className="w-4 h-4" />
          Planos de Ação 5W2H (PGR)
        </button>
      </div>

      {/* Conteúdo da Aba 1: Quadro da CIPA */}
      {activeTab === 'quadro' && (
        <div className="space-y-6">
          {/* Informação sobre Paridade e Estabilidade */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-xs flex items-start gap-3">
            <Award className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-xs text-gray-600 leading-relaxed">
              <strong className="text-gray-900">Composição Paritária (NR-5 item 5.4):</strong> A CIPA é
              composta por representantes do empregador e dos empregados. Os representantes eleitos pelos
              empregados contam com estabilidade provisória no emprego desde o registro da candidatura até 1 ano após o término do mandato (CLT Art. 165).
            </div>
          </div>

          {loadingMembros ? (
            <div className="text-center py-12 text-xs text-gray-400">
              Carregando quadro da CIPA...
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Bancada do Empregador */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-xs p-5 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-gray-600" />
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">
                        Bancada do Empregador (Designados)
                      </h3>
                      <p className="text-[11px] text-gray-500">
                        Representantes indicados pela diretoria
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-gray-700 bg-gray-100 px-2.5 py-0.5 rounded-full">
                    {membrosEmpregador.length} membros
                  </span>
                </div>

                {membrosEmpregador.length === 0 ? (
                  <div className="text-center py-8 text-xs text-gray-400">
                    Nenhum representante indicado pelo empregador até o momento.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {membrosEmpregador.map((m) => (
                      <div
                        key={m.id}
                        className="p-3.5 rounded-xl border border-gray-100 bg-gray-50/60 flex items-center justify-between hover:border-gray-300 transition-colors"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-gray-900">
                              {m.colaborador_nome || `Colaborador #${m.colaborador_id}`}
                            </span>
                            {m.cargo_cipa === 'PRESIDENTE' && (
                              <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full">
                                Presidente
                              </span>
                            )}
                            {m.cargo_cipa === 'SECRETARIO' && (
                              <span className="text-[10px] font-bold text-blue-800 bg-blue-100 px-2 py-0.5 rounded-full">
                                Secretário(a)
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] text-gray-500 mt-0.5 block">
                            {m.tipo_membro} • {m.colaborador_cargo || 'Cargo não informado'}
                          </span>
                        </div>
                        <span className="text-[10px] font-semibold text-gray-400 bg-white border border-gray-200 px-2 py-0.5 rounded">
                          {m.tipo_membro}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bancada dos Empregados */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-xs p-5 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-emerald-600" />
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">
                        Bancada dos Empregados (Eleitos)
                      </h3>
                      <p className="text-[11px] text-gray-500">
                        Representantes eleitos em escrutínio secreto
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-emerald-800 bg-emerald-50 px-2.5 py-0.5 rounded-full">
                    {membrosEmpregados.length} membros
                  </span>
                </div>

                {membrosEmpregados.length === 0 ? (
                  <div className="text-center py-8 text-xs text-gray-400">
                    Nenhum representante eleito homologado ainda.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {membrosEmpregados.map((m) => (
                      <div
                        key={m.id}
                        className="p-3.5 rounded-xl border border-emerald-100 bg-emerald-50/30 flex items-center justify-between hover:border-emerald-300 transition-colors"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-gray-900">
                              {m.colaborador_nome || `Colaborador #${m.colaborador_id}`}
                            </span>
                            {m.cargo_cipa === 'VICE_PRESIDENTE' && (
                              <span className="text-[10px] font-bold text-indigo-800 bg-indigo-100 px-2 py-0.5 rounded-full">
                                Vice-Presidente
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] text-gray-500 mt-0.5 block">
                            {m.tipo_membro} • {m.colaborador_cargo || 'Cargo não informado'}
                          </span>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {m.estabilidade_ativa && (
                            <span className="text-[9px] font-bold text-emerald-800 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded">
                              Estabilidade CLT
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Conteúdo da Aba 2: Calendário e Atas */}
      {activeTab === 'reunioes' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-gray-900">
                Calendário Obrigatório de Reuniões Ordinárias
              </h2>
              <p className="text-xs text-gray-500">
                12 reuniões ordinárias mensais com registro de presença digital e ata
              </p>
            </div>
          </div>

          {loadingReunioes ? (
            <div className="text-center py-12 text-xs text-gray-400">
              Carregando calendário de reuniões...
            </div>
          ) : reunioes.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-xs text-gray-400">
              Nenhuma reunião cadastrada no calendário deste mandato.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reunioes.map((r) => {
                const isRealizada = r.status === 'REALIZADA' || r.status === 'CONCLUIDA';

                return (
                  <div
                    key={r.id}
                    className={`rounded-2xl border p-4 shadow-xs flex flex-col justify-between transition-all ${
                      isRealizada
                        ? 'bg-emerald-50/40 border-emerald-200'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                          {r.numero_reuniao}ª {r.tipo === 'ORDINARIA' ? 'Ordinária' : 'Extraordinária'}
                        </span>
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            isRealizada
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {r.status}
                        </span>
                      </div>

                      <div className="text-xs font-bold text-gray-900 flex items-center gap-1.5 mb-1">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        {new Date(r.data_hora).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </div>

                      <p className="text-xs text-gray-600 line-clamp-2 mt-1">
                        {r.pauta || 'Reunião mensal de acompanhamento da CIPA.'}
                      </p>
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                      <span className="text-[11px] text-gray-500">
                        {r.presencas?.filter((p) => p.presente).length || 0} presentes
                      </span>

                      {isRealizada ? (
                        <button
                          onClick={() => setSelectedReuniaoAta(r)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-emerald-700 bg-white hover:bg-emerald-50 border border-emerald-200 rounded-lg transition-colors"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          Visualizar Ata
                        </button>
                      ) : (
                        <span className="text-[11px] text-gray-400">Ata pendente</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Conteúdo da Aba 3: Processo Eleitoral & Urna Digital */}
      {activeTab === 'eleicao' && (
        <div className="space-y-6">
          {loadingProcesso ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-xs text-gray-400">
              Carregando processo eleitoral...
            </div>
          ) : !processo ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
              <Vote className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-bold text-gray-800">
                Nenhum processo eleitoral ativo no momento
              </h3>
              <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
                As eleições da CIPA ocorrem anualmente no período que antecede o término da gestão atual.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xs space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-gray-200">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                      Eleição CIPA #{processo.id}
                    </span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-600 font-medium">Status: {processo.status}</span>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mt-1">
                    Eleição dos Representantes dos Empregados
                  </h3>
                </div>

                {processo.status === 'VOTACAO_ABERTA' && (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-2">
                    <Vote className="w-4 h-4 text-emerald-600" />
                    Urna Eletrônica Aberta para Votação
                  </div>
                )}
              </div>

              {/* Quórum Tracker */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-gray-800">
                    Acompanhamento de Quórum (Meta NR-5: {processo.quorum_esperado || 50}%)
                  </span>
                  <span className="text-gray-600 font-medium">
                    {processo.votos_apurados || 0} de {processo.quorum_esperado || 0} esperados ({quorumPorcentagem}%)
                  </span>
                </div>
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                    style={{ width: `${quorumPorcentagem}%` }}
                  />
                </div>
              </div>

              {/* Informações aos Colaboradores */}
              <div className="bg-emerald-50/50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
                <Info className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-xs text-emerald-900 leading-relaxed">
                  <strong>Acesso dos Empregados:</strong> Os colaboradores podem registrar sua candidatura e votar sigilosamente através do <strong>Portal do Colaborador</strong>. A urna possui garantia criptográfica de sigilo absoluto.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Conteúdo da Aba 4: Planos de Ação 5W2H */}
      {activeTab === 'acoes' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-gray-900">
                Planos de Ação 5W2H & Mitigações de Riscos
              </h2>
              <p className="text-xs text-gray-500">
                Ações corretivas levantadas pela CIPA e sincronizadas ao Inventário do PGR (NR-1)
              </p>
            </div>
          </div>

          {loadingAcoes ? (
            <div className="text-center py-12 text-xs text-gray-400">
              Carregando planos de ação...
            </div>
          ) : acoes.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-xs text-gray-400">
              Nenhuma ação 5W2H registrada para este mandato.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {acoes.map((acao) => (
                <div
                  key={acao.id}
                  className="bg-white rounded-2xl border border-gray-200 p-5 shadow-xs space-y-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            acao.prioridade === 'CRITICA'
                              ? 'bg-rose-100 text-rose-800'
                              : acao.prioridade === 'ALTA'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {acao.prioridade}
                        </span>
                        <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                          {acao.status}
                        </span>
                      </div>
                      <h3 className="text-sm font-bold text-gray-900 mt-2">{acao.titulo || acao.o_que}</h3>
                    </div>
                  </div>

                  <p className="text-xs text-gray-600 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                    <strong>Desvio:</strong> {acao.descricao_perigo}
                  </p>

                  <div className="grid grid-cols-2 gap-2 text-[11px] text-gray-600">
                    <div>
                      <strong>Responsável:</strong> {acao.responsavel_nome || '—'}
                    </div>
                    <div>
                      <strong>Prazo:</strong>{' '}
                      {new Date(acao.quando_prazo).toLocaleDateString('pt-BR')}
                    </div>
                    <div>
                      <strong>Local / Setor:</strong> {acao.onde_local || 'Geral'}
                    </div>
                    <div>
                      <strong>Custo:</strong>{' '}
                      {acao.quanto_custo ? `R$ ${Number(acao.quanto_custo).toFixed(2)}` : 'R$ 0,00'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal: Visualizar Ata Oficial */}
      {selectedReuniaoAta && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-fade-in">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between bg-gray-50/50">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    Ata da {selectedReuniaoAta.numero_reuniao}ª Reunião Ordinária da CIPA
                  </h3>
                  <p className="text-xs text-gray-500">
                    Data:{' '}
                    {new Date(selectedReuniaoAta.data_hora).toLocaleDateString('pt-BR')} • Norma
                    Regulamentadora nº 05
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedReuniaoAta(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-5 text-xs">
              <div>
                <span className="font-bold text-gray-900 block mb-1">Pauta da Reunião</span>
                <p className="bg-gray-50 p-3 rounded-xl border border-gray-200 text-gray-700 leading-relaxed">
                  {selectedReuniaoAta.pauta}
                </p>
              </div>

              <div>
                <span className="font-bold text-gray-900 block mb-1">Deliberações e Acordos</span>
                <p className="bg-gray-50 p-3 rounded-xl border border-gray-200 text-gray-700 leading-relaxed">
                  {selectedReuniaoAta.deliberacoes || 'Nenhuma deliberação registrada.'}
                </p>
              </div>

              <div>
                <span className="font-bold text-gray-900 block mb-1">Lista de Presença</span>
                <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
                  {selectedReuniaoAta.presencas?.map((p) => (
                    <div
                      key={p.id}
                      className="p-2.5 px-3 flex items-center justify-between bg-white text-[11px]"
                    >
                      <span className="font-semibold text-gray-900">
                        {p.membro_nome || `Membro #${p.membro_id}`}
                      </span>
                      <span
                        className={`font-bold px-2 py-0.5 rounded ${
                          p.presente
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-rose-50 text-rose-700'
                        }`}
                      >
                        {p.presente ? 'Presente' : 'Ausente'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50/50 flex items-center justify-end gap-2">
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg shadow-2xs transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Imprimir Ata
              </button>
              <button
                onClick={() => setSelectedReuniaoAta(null)}
                className="px-4 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Indicar Bancada do Empregador */}
      {showBancadaModal && mandato && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md overflow-hidden animate-fade-in">
            <form onSubmit={handleSalvarBancada}>
              <div className="p-5 border-b border-gray-200 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-base font-bold text-gray-900">
                    Indicar Bancada do Empregador
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowBancadaModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="p-5 space-y-4 text-xs">
                <p className="text-gray-500">
                  Informe os IDs dos colaboradores que representarão a organização nesta gestão.
                </p>

                <div>
                  <label className="block font-semibold text-gray-700 mb-1">
                    IDs dos Titulares (separados por vírgula) <span className="text-red-500 font-bold">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: 10, 15"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    value={bancadaTitulares}
                    onChange={(e) => setBancadaTitulares(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block font-semibold text-gray-700 mb-1">
                    IDs dos Suplentes (separados por vírgula)
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: 22, 28"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    value={bancadaSuplentes}
                    onChange={(e) => setBancadaSuplentes(e.target.value)}
                  />
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 bg-gray-50/50 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowBancadaModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvandoBancada}
                  className="px-4 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-sm disabled:opacity-50"
                >
                  {salvandoBancada ? 'Salvando...' : 'Salvar Bancada'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
